import { ajvResolver } from "@hookform/resolvers/ajv";
import ListIcon from "@mui/icons-material/List";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  TextField,
} from "@mui/material";
import { FormAlert, FormHeader } from "components";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import OrganizationSchema, {
  IOrganizationFormData,
} from "schema/security/organization";
import api from "services/api";
import { useStyles } from "./styles";

interface IRouteParams {
  id: string;
}

const OrganizationForm: React.FC = () => {
  const [mainError, setMainError] = useState("");
  const [statesA, setStatesA] = useState([]);
  const [citiesB, setCitiesB] = useState([]);

  const params = useParams<IRouteParams>();
  const firstInputElement = useRef<any>(null);
  const classes = useStyles();
  const history = useHistory();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control,
  } = useForm<IOrganizationFormData>({
    resolver: ajvResolver(OrganizationSchema),
    defaultValues: {
      federalTaxId: "",
      corporateName: "",
      tradingName: "",
      zipCode: "",
      address: "",
      number: "",
      complement: "",
      district: "",
      stateId: "",
      cityId: "",
      cnae: "",
      telephone: "",
      email: "",
      snowflakeAccount: "",
      isCustomer: false,
      blocked: false,
      isInactive: false,
    },
  });

  // initial load

  useEffect(() => {
    async function loadData() {
      // select Estado

      await api
        .post("/states/select")
        .then((response) => {
          const { data } = response.data;

          return data;
        })
        .then((statesResult) => {
          setStatesA(statesResult);
        })
        .catch((error) => {
          console.log(error);
          return error;
        });

      // select Cidade

      await api
        .post("/cities/select")
        .then((response) => {
          const { data } = response.data;

          return data;
        })
        .then((citiesResult) => {
          setCitiesB(citiesResult);
        })
        .catch((error) => {
          console.log(error);
          return error;
        });
    }

    loadData();
  }, []);

  // main data load

  useEffect(() => {
    async function loadData() {
      const { id } = params;

      // form data

      await api
        .get(`/organizations/${id}`)
        .then((response) => {
          const { data } = response.data;

          const organizationResult: IOrganizationFormData = {
            id: data.id,
            federalTaxId: data.federalTaxId,
            corporateName: data?.corporateName,
            tradingName: data.tradingName,
            zipCode: data.zipCode,
            address: data.address,
            number: data.number,
            complement: data.complement,
            district: data.district,
            stateId: data.stateId.id,
            cityId: data.cityId.id,
            cnae: data.cnae,
            telephone: data.telephone,
            email: data.email,
            snowflakeAccount: data.snowflakeAccount,
            isCustomer: data.isCustomer,
            blocked: data.blocked,
            isInactive: data.isInactive,
          };

          return organizationResult;
        })
        .then((organizationResult: IOrganizationFormData) => {
          reset(organizationResult);
        })
        .catch((error) => {
          console.log(error);
          return error;
        });
    }

    if (params.id) {
      loadData();
    }
  }, [params, params.id]);

  // data save

  const onSubmit: SubmitHandler<IOrganizationFormData> = useCallback(
    async (data) => {
      const payLoad: IOrganizationFormData = {
        id: "",
        federalTaxId: data.federalTaxId,
        corporateName: data?.corporateName,
        tradingName: data.tradingName,
        zipCode: data.zipCode,
        address: data.address,
        number: data.number,
        complement: data.complement,
        district: data.district,
        stateId: data.stateId,
        cityId: data.cityId,
        cnae: data.cnae,
        telephone: data.telephone,
        email: data.email,
        snowflakeAccount: data.snowflakeAccount,
        isCustomer: data.isCustomer,
        blocked: data.blocked,
        isInactive: data.isInactive,
      };

      if (params.id) {
        const { id } = params;

        payLoad.id = id;

        await api
          .put(`/organizations`, payLoad)
          .then(history.push("/organizations"))
          .catch((error) => {
            console.log(error.response?.data);
            setMainError(error.response?.data.data.name);
            return error.response?.data.data;
          });
      } else {
        await api
          .post("/organizations", payLoad)
          .then(history.push("/organizations/new"))
          .then(() => reset())
          .then(() =>
            setTimeout(() => {
              firstInputElement.current.focus();
            }, 0)
          )
          .catch((error) => {
            console.log(error.response?.data);
            setMainError(error.response?.data.data.name);
            return error.response?.data.data;
          });
      }
    },
    []
  );

  const handleChange = (formField: any) => {
    setMainError("");
  };

  return (
    <Paper elevation={0} className={classes.paper}>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        data-testid="form"
      >
        <FormHeader
          title="Organizações"
          icon={ListIcon}
          backRoute="/organizations"
          showSaveButton={true}
          helpText="Dados coletados sobre as organizações."
        />

        <FormAlert setMainError={setMainError} mainError={mainError} />

        <Grid
          container
          spacing={1}
          className={
            mainError === ""
              ? classes.formContainer
              : classes.formContainerWithError
          }
        >
          <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
            <InputLabel htmlFor="federalTaxId">CNPJ</InputLabel>
            <TextField
              id="federalTaxId"
              error={!!errors.federalTaxId}
              helperText={errors?.federalTaxId?.message}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth={true}
              autoFocus
              inputRef={firstInputElement}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 20,
              }}
              {...register("federalTaxId", {
                onChange: (e) => handleChange(e),
              })}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
            <InputLabel htmlFor="corporateName">Razão Social</InputLabel>
            <TextField
              id="corporateName"
              error={!!errors?.corporateName}
              helperText={errors?.corporateName?.message}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 100,
              }}
              {...register("corporateName", {
                onChange: (e) => handleChange(e),
              })}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
            <InputLabel htmlFor="tradingName">Nome Fantasia</InputLabel>
            <TextField
              id="tradingName"
              error={!!errors.tradingName}
              helperText={errors?.tradingName?.message}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 100,
              }}
              {...register("tradingName", { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
            <InputLabel htmlFor="zipCode">CEP</InputLabel>
            <TextField
              id="zipCode"
              error={!!errors.zipCode}
              helperText={errors?.zipCode?.message}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 10,
              }}
              {...register("zipCode", { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
            <InputLabel htmlFor="address">Endereço</InputLabel>
            <TextField
              id="address"
              error={!!errors.address}
              helperText={errors?.address?.message}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 100,
              }}
              {...register("address", { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
            <InputLabel htmlFor="number">Número</InputLabel>
            <TextField
              id="number"
              error={!!errors.number}
              helperText={errors?.number?.message}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 10,
              }}
              {...register("number", { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
            <InputLabel htmlFor="complement">Complemento</InputLabel>
            <TextField
              id="complement"
              error={!!errors.complement}
              helperText={errors?.complement?.message}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 100,
              }}
              {...register("complement", { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
            <InputLabel htmlFor="district">Bairro</InputLabel>
            <TextField
              id="district"
              error={!!errors.district}
              helperText={errors?.district?.message}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 100,
              }}
              {...register("district", { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
            <InputLabel htmlFor="stateId">Estado</InputLabel>
            <TextField
              id="stateId"
              error={!!errors.stateId}
              helperText={errors?.stateId?.message}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth={true}
              value={`${watch().stateId}`}
              select
              {...register("stateId", {
                onChange: (e) => {
                  setValue("stateId", e.target.value);
                  handleChange(e);
                },
              })}
            >
              {statesA.map((state: any) => (
                <MenuItem key={state.id} value={state.id}>
                  {state.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
            <InputLabel htmlFor="cityId">Cidade</InputLabel>
            <TextField
              id="cityId"
              error={!!errors.cityId}
              helperText={errors?.cityId?.message}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth={true}
              value={`${watch().cityId}`}
              select
              {...register("cityId", {
                onChange: (e) => {
                  setValue("cityId", e.target.value);
                  handleChange(e);
                },
              })}
            >
              {citiesB.map((city: any) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
            <InputLabel htmlFor="cnae">CNAE Principal</InputLabel>
            <TextField
              id="cnae"
              error={!!errors.cnae}
              helperText={errors?.cnae?.message}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 10,
              }}
              {...register("cnae", { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
            <InputLabel htmlFor="telephone">Telefone</InputLabel>
            <TextField
              id="telephone"
              error={!!errors.telephone}
              helperText={errors?.telephone?.message}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 100,
              }}
              {...register("telephone", { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
            <InputLabel htmlFor="email">Email</InputLabel>
            <TextField
              id="email"
              error={!!errors.email}
              helperText={errors?.email?.message}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 100,
              }}
              {...register("email", { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <InputLabel htmlFor="snowflakeAccount">Conta Snowflake</InputLabel>
            <TextField
              id="snowflakeAccount"
              error={!!errors.snowflakeAccount}
              helperText={errors?.snowflakeAccount?.message}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 100,
              }}
              {...register("snowflakeAccount", {
                onChange: (e) => handleChange(e),
              })}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
            <FormGroup className={classes.checkBox}>
              <Controller
                name="isCustomer"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(e) => field.onChange(e.target.checked)}
                        checked={field.value}
                      />
                    }
                    label="Cliente"
                    className={classes.checkBoxLabel}
                  />
                )}
              />
            </FormGroup>
          </Grid>

          <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
            <FormGroup className={classes.checkBox}>
              <Controller
                name="blocked"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(e) => field.onChange(e.target.checked)}
                        checked={field.value}
                      />
                    }
                    label="Bloqueado"
                    className={classes.checkBoxLabel}
                  />
                )}
              />
            </FormGroup>
          </Grid>

          <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
            <FormGroup className={classes.checkBox}>
              <Controller
                name="isInactive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(e) => field.onChange(e.target.checked)}
                        checked={field.value}
                      />
                    }
                    label="Inativo"
                    className={classes.checkBoxLabel}
                  />
                )}
              />
            </FormGroup>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default OrganizationForm;
